// ─────────────────────────────────────────────────────────
// Einstellungen → Arbeitszeiten (Arbeitszeitmodelle, plancraft-Stil)
// ─────────────────────────────────────────────────────────
// Eigenes Modul, damit CompanyDashboard.js nicht weiter wächst.
// Deutsche UI. Nutzt /api/working-time-models (org-scoped, worktime:read/write).
// Phase 1: ein Wochenraster (Mo–So) je Modell + Standard-Flag + Mitarbeiter-Zuweisung.
// „Saisonale Einstellungen" (mehrere Zeiträume) folgen als Phase 2 — das Backend
// trägt die Saison-Ebene bereits, hier wird nur die eine Ganzjahres-Saison bedient.
//
// Live-Berechnung der Gesamtzeit läuft OHNE draw() direkt auf dem DOM
// (recalcWorkTimeTotals), damit Text-/Checkbox-Eingaben beim Tippen nicht zurückgesetzt werden.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
// WEEKDAY_LABELS und "Januar – Dezember" (Zeitraum-Spalte) bleiben bewusst unübersetzt:
// deutsche Wochentags-/Monatsnamen gehören in utils/format.ts, nicht in eigene i18n-Keys.
import { icon, escapeHtml } from '../../utils/dom.js';
import { createWorkTimeModel, updateWorkTimeModel, deleteWorkTimeModel } from '../../core/org.js';
import { t } from '../../i18n/index.js';

const WEEKDAY_LABELS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const BREAK_OPTIONS = [0, 15, 30, 45, 60, 90];
const STEP_MIN = 15; // Raster der Zeit-Dropdowns
const MIN_PER_DAY = 24 * 60;

// ── Formatierung ──────────────────────────────────────────
function minToLabel(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function fmtDuration(min) {
  const unit = t('settings.workTime.hoursUnit');
  if (!min || min <= 0) return `0:00 ${unit}`;
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} ${unit}`;
}
function netDayMinutes(d) {
  if (d.startMin == null || d.endMin == null) return 0;
  const net = d.endMin - d.startMin - (d.breakMin || 0);
  return net > 0 ? net : 0;
}
function weekMinutes(days) {
  return (days || []).reduce((sum, d) => sum + netDayMinutes(d), 0);
}

// ── Standard-Wochenraster (Mo–Fr 07:00–16:00, 60 Min Pause = 40 Std.) ──
function defaultDays() {
  const days = [];
  for (let w = 0; w < 7; w++) {
    if (w <= 4) days.push({ weekday: w, startMin: 7 * 60, endMin: 16 * 60, breakMin: 60 });
    else days.push({ weekday: w, startMin: null, endMin: null, breakMin: 0 });
  }
  return days;
}

// ── Options-Generatoren ───────────────────────────────────
function timeOptions(sel) {
  let out = `<option value=""${sel == null ? ' selected' : ''}>–</option>`;
  for (let m = 0; m <= MIN_PER_DAY; m += STEP_MIN) {
    out += `<option value="${m}"${sel === m ? ' selected' : ''}>${minToLabel(m)}</option>`;
  }
  return out;
}
function breakOptions(sel) {
  const list = BREAK_OPTIONS.includes(sel) ? BREAK_OPTIONS : [sel, ...BREAK_OPTIONS];
  return list
    .map((b) => `<option value="${b}"${sel === b ? ' selected' : ''}>${escapeHtml(b === 0 ? t('settings.workTime.noBreak') : t('settings.workTime.breakMinutes', { minutes: b }))}</option>`)
    .join('');
}

// ── Mitarbeiter-Avatare (Stack) für die Listen-Spalte ─────
function memberAvatars(members, ctx) {
  const { grad, initials } = ctx;
  if (!members || !members.length) return '<span class="cd-wtm-none">–</span>';
  const shown = members.slice(0, 5);
  const rest = members.length - shown.length;
  const av = shown
    .map((m) => `<span class="cd-wtm-av" title="${escapeHtml(m.name)}" style="background:${grad(m.name)}">${escapeHtml(initials(m.name, ''))}</span>`)
    .join('');
  return `<div class="cd-wtm-avstack">${av}${rest > 0 ? `<span class="cd-wtm-av cd-wtm-av-more">+${rest}</span>` : ''}</div>`;
}

// ── Listen-Bereich ────────────────────────────────────────
export function renderWorkTimeSection(ctx) {
  const { s, can } = ctx;
  if (!can('worktime:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.wtmLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const models = s.workTimeModels || [];
  const info = `
    <div class="cd-emp-info">${icon('info', 18)}<span>${escapeHtml(t('settings.workTime.info'))}</span></div>`;

  let body;
  if (!models.length) {
    body = `<div class="cd-emp-empty">${escapeHtml(t('settings.workTime.empty'))}</div>`;
  } else {
    const rows = models
      .map((m) => {
        const clickable = can('worktime:write');
        const clickAttr = clickable ? ` data-action="wtm-edit-open" data-id="${escapeHtml(m.id)}"` : '';
        const badge = m.isDefault ? ` <span class="cd-wtm-badge">${escapeHtml(t('settings.workTime.defaultBadge'))}</span>` : '';
        return `
          <div class="cd-wtm-trow${clickable ? ' cd-emp-trow-click' : ''}"${clickAttr}>
            <div class="cd-wtm-name">${icon('schedule', 18)}<span>${escapeHtml(m.name)}${badge}</span></div>
            <div>${escapeHtml(fmtDuration(m.weeklyMinutes))} ${escapeHtml(t('settings.workTime.perWeek'))}</div>
            <div class="cd-emp-mail">Januar – Dezember</div>
            <div>${memberAvatars(m.members, ctx)}</div>
          </div>`;
      })
      .join('');
    body = `
      <div class="cd-emp-table">
        <div class="cd-wtm-thead"><span>${escapeHtml(t('common.name'))}</span><span>${escapeHtml(t('settings.workTime.colWorkTimes'))}</span><span>${escapeHtml(t('settings.workTime.colPeriod'))}</span><span>${escapeHtml(t('settings.workTime.colEmployees'))}</span></div>
        ${rows}
      </div>`;
  }

  return `
    <div class="cd-emp">
      ${info}
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('schedule', 18)}<h3>${escapeHtml(t('settings.workTime.title'))}</h3></div>
        ${body}
      </div>
    </div>`;
}

// ── Kopf-Button „Arbeitszeitmodell erstellen" ─────────────
export function workTimeHeadButton(ctx) {
  if (!ctx.can('worktime:write')) return '';
  return `<button class="cd-btn-primary" data-action="wtm-create-open">${icon('add', 16)}${escapeHtml(t('settings.workTime.createButton'))}</button>`;
}

// ── Modal (Erstellen / Bearbeiten) ────────────────────────
export function renderWorkTimeModal(ctx) {
  const { s } = ctx;
  const e = s.wtmEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const isEdit = !!e.id;
  const members = s.members || [];

  const memberChips = members.length
    ? members
        .map((m) => {
          const label = m.name || m.email;
          const checked = e.memberIds.includes(m.id) ? 'checked' : '';
          return `
            <label class="cd-wtm-mchip">
              <input type="checkbox" data-wtm-member value="${escapeHtml(m.id)}" ${checked} ${dis}>
              <span>${escapeHtml(label)}</span>
            </label>`;
        })
        .join('')
    : `<div class="cd-wtm-none">${escapeHtml(t('settings.workTime.noEmployees'))}</div>`;

  const dayRows = e.days
    .map((d) => {
      const w = d.weekday;
      const total = d.startMin != null && d.endMin != null ? fmtDuration(netDayMinutes(d)) : '–';
      return `
        <div class="cd-wtm-drow">
          <div class="cd-wtm-dname">${WEEKDAY_LABELS[w]}</div>
          <div class="cd-select-shell"><select id="wtm-d${w}-start" class="cd-input" data-wtm-recalc ${dis}>${timeOptions(d.startMin)}</select>${icon('expand_more', 16)}</div>
          <div class="cd-select-shell"><select id="wtm-d${w}-end" class="cd-input" data-wtm-recalc ${dis}>${timeOptions(d.endMin)}</select>${icon('expand_more', 16)}</div>
          <div class="cd-select-shell"><select id="wtm-d${w}-break" class="cd-input" data-wtm-recalc ${dis}>${breakOptions(d.breakMin)}</select>${icon('expand_more', 16)}</div>
          <div class="cd-wtm-dtotal" id="wtm-d${w}-total">${total}</div>
        </div>`;
    })
    .join('');

  const footBtns = isEdit
    ? (e.confirmDelete
        ? `<div class="cd-wtm-confirm"><span>${escapeHtml(t('settings.workTime.deleteConfirm'))}</span>
             <button class="cd-btn-ghost" data-action="wtm-delete-cancel" ${dis}>${escapeHtml(t('common.cancel'))}</button>
             <button class="cd-btn-danger" data-action="wtm-delete-confirm" ${dis}>${escapeHtml(e.saving ? t('common.deleting') : t('common.confirmDelete'))}</button></div>`
        : `<button class="cd-btn-danger-ghost" data-action="wtm-delete" ${dis}>${icon('delete', 16)}${escapeHtml(t('settings.workTime.deleteButton'))}</button>
           <div class="cd-wtm-foot-spacer"></div>
           <button class="cd-btn-ghost" data-action="wtm-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
           <button class="cd-btn-primary" data-action="wtm-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`)
    : `<div class="cd-wtm-foot-spacer"></div>
       <button class="cd-btn-ghost" data-action="wtm-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
       <button class="cd-btn-primary" data-action="wtm-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="wtm-close">
      <div class="cd-emp-modal cd-wtm-modal" data-action="wtm-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('schedule', 20)}${escapeHtml(isEdit ? t('settings.workTime.modalTitleEdit') : t('settings.workTime.modalTitleCreate'))}</h2>
          <button class="cd-emp-modal-x" data-action="wtm-close" title="${escapeHtml(t('common.close'))}">${icon('close', 20)}</button>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.workTime.nameLabel'))}</span><input class="cd-input" id="wtm-name" type="text" placeholder="${escapeHtml(t('settings.workTime.namePlaceholder'))}" value="${escapeHtml(e.name || '')}" ${dis}></label>
            <label class="cd-wtm-defaultbox"><input type="checkbox" id="wtm-default" ${e.isDefault ? 'checked' : ''} ${dis}><span>${escapeHtml(t('settings.workTime.setDefault'))}</span></label>
          </div>

          <div class="cd-form-section">${escapeHtml(t('settings.workTime.assignedEmployees'))}</div>
          <div class="cd-wtm-mlist">${memberChips}</div>

          <div class="cd-form-section">${escapeHtml(t('settings.workTime.workTimeSectionTitle'))}</div>
          <div class="cd-wtm-grid">
            <div class="cd-wtm-ghead"><span>${escapeHtml(t('settings.workTime.colDay'))}</span><span>${escapeHtml(t('settings.workTime.colStart'))}</span><span>${escapeHtml(t('settings.workTime.colEnd'))}</span><span>${escapeHtml(t('settings.workTime.colBreak'))}</span><span>${escapeHtml(t('settings.workTime.colTotal'))}</span></div>
            ${dayRows}
            <div class="cd-wtm-gfoot"><span>${escapeHtml(t('settings.workTime.weeklyHours'))}</span><span id="wtm-week-total">${fmtDuration(weekMinutes(e.days))}</span></div>
          </div>
        </div>
        <div class="cd-emp-modal-foot">${footBtns}</div>
      </div>
    </div>`;
}

// ── Live-Neuberechnung der Gesamtzeiten (direkt am DOM, ohne draw) ──
export function recalcWorkTimeTotals() {
  let week = 0;
  for (let w = 0; w < 7; w++) {
    const start = document.getElementById(`wtm-d${w}-start`);
    const end = document.getElementById(`wtm-d${w}-end`);
    const brk = document.getElementById(`wtm-d${w}-break`);
    const totalEl = document.getElementById(`wtm-d${w}-total`);
    if (!start || !end || !totalEl) continue;
    const startMin = start.value === '' ? null : parseInt(start.value, 10);
    const endMin = end.value === '' ? null : parseInt(end.value, 10);
    const breakMin = brk ? parseInt(brk.value || '0', 10) : 0;
    if (startMin == null || endMin == null) {
      totalEl.textContent = '–';
      continue;
    }
    const net = netDayMinutes({ startMin, endMin, breakMin });
    totalEl.textContent = fmtDuration(net);
    week += net;
  }
  const weekEl = document.getElementById('wtm-week-total');
  if (weekEl) weekEl.textContent = fmtDuration(week);
}

// ── Formular vom DOM lesen (Submit) ───────────────────────
function readDays() {
  const days = [];
  let error = null;
  for (let w = 0; w < 7; w++) {
    const sv = document.getElementById(`wtm-d${w}-start`)?.value ?? '';
    const ev = document.getElementById(`wtm-d${w}-end`)?.value ?? '';
    const bv = document.getElementById(`wtm-d${w}-break`)?.value ?? '0';
    const startMin = sv === '' ? null : parseInt(sv, 10);
    const endMin = ev === '' ? null : parseInt(ev, 10);
    const breakMin = parseInt(bv, 10) || 0;
    if ((startMin == null) !== (endMin == null)) {
      error = t('settings.workTime.errorStartEndBoth', { weekday: WEEKDAY_LABELS[w] });
    } else if (startMin != null && endMin != null && endMin <= startMin) {
      error = t('settings.workTime.errorEndBeforeStart', { weekday: WEEKDAY_LABELS[w] });
    } else if (startMin != null && endMin != null && endMin - startMin - breakMin < 0) {
      error = t('settings.workTime.errorBreakExceeds', { weekday: WEEKDAY_LABELS[w] });
    }
    days.push({ weekday: w, startMin, endMin, breakMin });
  }
  return { days, error };
}

// ── Aktions-Handler (gibt true zurück, wenn behandelt) ────
export function handleWorkTimeAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'wtm-noop':
      return true;
    case 'wtm-create-open':
      if (!ctx.can('worktime:write')) return true;
      s.wtmEdit = {
        open: true, id: null, name: '', isDefault: !(s.workTimeModels || []).length,
        memberIds: [], days: defaultDays(), saving: false, error: '', confirmDelete: false,
      };
      draw();
      return true;
    case 'wtm-edit-open': {
      if (!ctx.can('worktime:write')) return true;
      const id = el.dataset.id;
      const model = (s.workTimeModels || []).find((m) => m.id === id);
      if (!model) return true;
      s.wtmEdit = {
        open: true, id, name: model.name, isDefault: model.isDefault,
        memberIds: [...(model.memberIds || [])],
        days: model.days.map((d) => ({ weekday: d.weekday, startMin: d.startMin, endMin: d.endMin, breakMin: d.breakMin })),
        saving: false, error: '', confirmDelete: false,
      };
      draw();
      return true;
    }
    case 'wtm-close':
      s.wtmEdit = null;
      draw();
      return true;
    case 'wtm-submit': {
      const e = s.wtmEdit;
      if (!e) return true;
      const name = (document.getElementById('wtm-name')?.value || '').trim();
      const isDefault = !!document.getElementById('wtm-default')?.checked;
      const memberIds = Array.from(document.querySelectorAll('[data-wtm-member]:checked')).map((x) => x.value);
      const { days, error } = readDays();
      if (!name) { e.error = t('settings.workTime.errorNameRequired'); draw(); return true; }
      if (error) { e.error = error; draw(); return true; }
      e.saving = true; e.error = ''; draw();
      const payload = { name, isDefault, memberIds, days };
      const req = e.id ? updateWorkTimeModel(e.id, payload) : createWorkTimeModel(payload);
      req
        .then(() => { s.wtmEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.wtmEdit) { s.wtmEdit.saving = false; s.wtmEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    case 'wtm-delete': {
      const e = s.wtmEdit;
      if (!e) return true;
      e.confirmDelete = true;
      draw();
      return true;
    }
    case 'wtm-delete-cancel': {
      const e = s.wtmEdit;
      if (!e) return true;
      e.confirmDelete = false;
      draw();
      return true;
    }
    case 'wtm-delete-confirm': {
      const e = s.wtmEdit;
      if (!e || !e.id) return true;
      e.saving = true; e.error = ''; draw();
      deleteWorkTimeModel(e.id)
        .then(() => { s.wtmEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.wtmEdit) { s.wtmEdit.saving = false; s.wtmEdit.confirmDelete = false; s.wtmEdit.error = err.message || t('common.deleteFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
